// Keeps Latin letters/digits AND Georgian (Mkhedruli) script characters
// (U+10A0-U+10FF) so a Georgian title produces a real Georgian slug instead
// of an empty string — Georgian has no case, so toLowerCase() is a no-op
// for it and only affects the Latin half.
const SLUG_STRIP = /[^a-z0-9Ⴀ-ჿ-]/g;

export function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(SLUG_STRIP, "");
}

// Slugifies `text`, then disambiguates against `existingItems` (objects with
// `.id` and a `field` property, default "slug") by appending -2, -3, ... if
// another item (other than `excludeId`, the one being edited) already has
// that slug.
export function uniqueSlug(text, existingItems, excludeId, field = "slug") {
  const base = slugify(text);
  if (!base) return base;
  const taken = new Set(
    existingItems
      .filter((item) => item.id !== excludeId)
      .map((item) => item[field])
      .filter(Boolean),
  );
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

// Computes both the English slug (from `name`) and Georgian slug (from
// `nameKa`), uniqueness-checked independently against each language's own
// field so an English and a Georgian slug never collide with a different
// item's slug in the same language. Falls back to the English slug if
// there's no Georgian name yet.
export function slugPair(name, nameKa, existingItems, excludeId) {
  const slug = uniqueSlug(name, existingItems, excludeId, "slug");
  const slugKa = nameKa
    ? uniqueSlug(nameKa, existingItems, excludeId, "slugKa")
    : slug;
  return { slug, slugKa };
}

// Picks the right slug for the current UI language, falling back to the
// other language's slug, then the raw Firestore doc id.
export function resolveSlug(item, language) {
  if (!item) return "";
  return (language === "ka" ? item.slugKa || item.slug : item.slug) || item.id;
}
